import { useTranslation } from '@dreamstack/i18n'
import { SimpleInput } from '@dreamstack/simple-components/src/lib/SimpleInput'
import { SimpleSelect } from '@dreamstack/simple-components/src/lib/SimpleSelect'
import { cn } from '@dreamstack/util'
import MenuItem from '@mui/material/MenuItem'
import type { CSSProperties, FunctionComponent } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNotificationContext } from '../notification/NotificationContext'
import { FormCheckbox } from '../forms/FormCheckbox'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { BodyText, HeadingH2 } from '../layout/Texts'
import { useIs24ContactFormSchema } from './useIs24ContactFormSchema'
import { useSubmitContactForm } from './useSubmitContactForm'

export const IS24ContactFormHeading: FunctionComponent<
  React.PropsWithChildren<{
    className?: string
    style?: CSSProperties
  }>
> = ({ className, style }) => {
  const t = useTranslation()
  return (
    <HeadingH2 className={cn('mb-px-16 md:mb-px-24', className)} style={style}>
      <span>{t('accentro:is24.learnMore')}</span>
    </HeadingH2>
  )
}

export const IS24ContactForm: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const t = useTranslation()
  const yupSchemaResolver = useIs24ContactFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const { handleSubmit, control, reset, watch } = methods
  const { addNotification } = useNotificationContext()

  const requestPhone = watch('requestPhone')
  const onSuccess = () => {
    addNotification('success', t('accentro:contactform.successMessage'))
    reset()
  }
  const { submitFn: onSubmit, loading } = useSubmitContactForm({
    successCallback: onSuccess,
    formId: 'immoscout24-contact',
    type: 'is24contactform',
    customSubject: 'ImmoScout24 Partnerschaft Kontaktanfrage',
  })
  return (
    <div>
      <IS24ContactFormHeading />
      <BodyText className="mb-px-24 md:mb-px-32">
        {t('accentro:is24.requestBrochure')}
      </BodyText>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-px-24 md:grid-cols-2">
            {/* <Controller
              defaultValue=""
              label={t('accentro:contactform.salutation')}
              name="clientSalutation"
              control={control}
              render={({field}) => <SimpleInput { ...field } fullWidth />}
            /> */}
            <Controller
              defaultValue=""
              name="clientSalutation"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SimpleSelect
                  label={t('accentro:contactform.salutation')}
                  formControlProps={{
                    variant: 'filled',
                    fullWidth: true,
                  }}
                  selectProps={{
                    disableUnderline: true,
                    onChange,
                    value,
                  }}
                >
                  {/* TODO: Localize */}
                  <MenuItem value={'Herr'}>{'Herr'}</MenuItem>
                  <MenuItem value={'Frau'}>{'Frau'}</MenuItem>
                </SimpleSelect>
              )}
            />
            <div className="col-start-1">
              <Controller
                defaultValue=""
                name="clientFirstName"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.firstname')}*`}
                    fullWidth
                  />
                )}
              />
            </div>
            <Controller
              defaultValue=""
              name="clientLastName"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={`${t('accentro:contactform.lastname')}*`}
                  fullWidth
                />
              )}
            />

            <div className="col-span-full">
              <Controller
                defaultValue=""
                name="clientCompanyName"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.companyName')}`}
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                defaultValue=""
                name="clientEmail"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.email')}*`}
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                defaultValue=""
                name="text"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    multiline
                    minRows={4}
                    label={`${t('accentro:contactform.message')}*`}
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <FormCheckbox
                propertyName="requestPhone"
                labelComponent={`${t('accentro:contactform.requestPhone')}`}
              />
            </div>
            {requestPhone && (
              <div className="col-span-full">
                <Controller
                  defaultValue=""
                  name="clientPhone"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={`${t('accentro:contactform.phone')}*`}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}
            <div className="col-span-full">
              <FormCheckbox
                propertyName="consent"
                labelComponent={`${t('accentro:contactform.consentText')}*`}
              />
            </div>
            <div className="col-span-full text-accentroGray-500">
              <p>{t('accentro:contactform.requiredFieldsText')}</p>
            </div>
            <SimpleMuiButton
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              size="small"
              className="w-[min-content] whitespace-nowrap"
            >
              {t('accentro:contactform.sendMessage')}
            </SimpleMuiButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
